import React from "react";

/**
 * Catches render errors so a single bad prop cannot blank the entire app.
 *
 * Without a boundary, React 18 unmounts the whole tree on an uncaught render
 * error and the user is left with a white page and nothing in the UI to act on.
 * That is what happened on the live display: a DOM attribute received an object,
 * React threw inside setValueForProperty, and the page went blank.
 *
 * It also records componentStack, which production React only exposes to a
 * boundary. That is the one thing that identifies which component failed when
 * the build is minified.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, componentStack: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    const componentStack = (errorInfo && errorInfo.componentStack) || "(none)";
    this.setState({ componentStack });
    // Host element names (div, img, ...) survive minification even when
    // component names do not, so this still narrows down the failing element.
    console.error(
      "[ErrorBoundary] " + (error && error.message) + "\ncomponentStack:" + componentStack
    );
    // Kept on window so it can be read back from the console after a crash.
    window.__lastReactError = {
      message: error && error.message,
      stack: error && error.stack,
      componentStack,
    };
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="container" style={{ padding: "2rem" }}>
        <div className="alert alert-danger" role="alert">
          <h4>Something went wrong displaying this page.</h4>
          <p>
            The rest of the app is still working - use the menu to go back, or
            reload the page.
          </p>
          <p style={{ fontSize: "0.85rem", marginBottom: 0 }}>
            {String(this.state.error && this.state.error.message)}
          </p>
        </div>
        <details style={{ whiteSpace: "pre-wrap", fontSize: "0.75rem" }}>
          <summary>Technical details</summary>
          {this.state.componentStack}
        </details>
      </div>
    );
  }
}

export default ErrorBoundary;
